import { Brain, Database, Lightbulb, MessageSquare, Sparkles, type LucideIcon } from 'lucide-react';
import type { AutomationStage } from '@/types';

/**
 * The onboarding automation pipeline, in the order the backend runs it.
 *
 * Lives in `src/constants` rather than inside the onboarding feature because
 * the setup surfaces that read it are spread across the app shell, chat,
 * content, discovery, dashboard and settings.
 */
export const VALID_STAGES: AutomationStage[] = [
  'analyzing_niche',
  'indexing_knowledge_base',
  'understanding_goals',
  'calibrating_tone',
  'finalizing_setup',
];

interface StageMeta {
  icon: LucideIcon;
  /** Full label, used in checklists and panels. */
  label: string;
  /** Compact label for the top bar and collapsed sidebar, where space is scarce. */
  shortLabel: string;
  /**
   * Plain-English fallback describing what LiLi is doing during this stage.
   * The backend's live message wins whenever it sends one; this keeps the UI
   * meaningful in the gap before the first message for a stage arrives.
   */
  description: string;
}

export const STAGE_META: Record<AutomationStage, StageMeta> = {
  analyzing_niche: {
    icon: Brain,
    label: 'Analyzing your niche',
    shortLabel: 'Analyzing niche',
    description: 'Researching what is trending in your industry right now',
  },
  indexing_knowledge_base: {
    icon: Database,
    label: 'Indexing your knowledge base',
    shortLabel: 'Indexing knowledge',
    description: 'Reading the documents and notes you uploaded',
  },
  understanding_goals: {
    icon: Lightbulb,
    label: 'Understanding your goals',
    shortLabel: 'Setting goals',
    description: 'Studying your social profiles and how you write',
  },
  calibrating_tone: {
    icon: MessageSquare,
    label: 'Calibrating tone & style',
    shortLabel: 'Calibrating tone',
    description: 'Learning your voice so your posts sound like you',
  },
  finalizing_setup: {
    icon: Sparkles,
    label: 'Finalizing your setup',
    shortLabel: 'Finalizing',
    description: 'Building your content strategy and writing your first posts',
  },
};
